// 261015 P4c-2b (D12/D13, "Chốt sau Dev review" Q2/Q8) — hiển thị "nhịp hấp dẫn dự kiến" của từng tập ở chặng ④. THUẦN: không antd, không react, không gọi API (jest chạy được).
//
// 🔴 Nguyên tắc (R5): nhãn Nhịp sướng / Lật kèo / Tiết lộ là NHÃN BIÊN TẬP DỰ KIẾN — một sự việc có trích được chỉ ra, là lời hứa với người xem. KHÔNG phải bằng chứng tập đã hay;
//    chữ ở đây không bao giờ nói "tập hay / tập nhạt". "Chưa chỉ ra" ≠ "nhạt"; "chưa kiểm" ≠ "chưa chỉ ra".
// 🔴 Trạng thái từng tập (CÓ NHỊP / CHƯA CHỈ RA / CHƯA KIỂM + lý do), độ phủ và các đoạn tập liền chưa chỉ ra nhịp đều do BE tính (`OutlineRecord.appeal`). FE chỉ ĐỌC: không phân loại, không so ngưỡng độ dài.
import type {
  AppealRun, EpisodeAppealKind, EpisodeAppealView, EpisodeClaim, EpisodeSplitOp, MotiveGroup, OutlineEpisode, OverviewMotives,
} from '../../../types/adaptation';
import { MOTIVE_GROUPS } from './overview/overviewView';

// ─── nhãn 3 loại ─────────────────────────────────────────────────────────────────────────────────────────────

export const APPEAL_KINDS: ReadonlyArray<EpisodeAppealKind> = ['SATISFACTION', 'REVERSAL', 'REVEAL'];
export const APPEAL_KIND_LABEL: Record<EpisodeAppealKind, string> = { SATISFACTION: 'Nhịp sướng', REVERSAL: 'Lật kèo', REVEAL: 'Tiết lộ' };
/** Mầu tag antd — không có đỏ (đỏ dành cho lỗi; nhãn này không phải lỗi hay điểm). */
export const APPEAL_KIND_COLOR: Record<EpisodeAppealKind, string> = { SATISFACTION: 'green', REVERSAL: 'gold', REVEAL: 'geekblue' };
export const APPEAL_KIND_HINT: Record<EpisodeAppealKind, string> = {
  SATISFACTION: 'Nhân vật chính thắng / được đền / kẻ xấu bị trả giá.',
  REVERSAL: 'Tình thế đảo chiều.',
  REVEAL: 'Bí mật / thân phận / sự thật lộ ra.',
};
export const isAppealKind = (v: unknown): v is EpisodeAppealKind => typeof v === 'string' && (APPEAL_KINDS as ReadonlyArray<string>).includes(v);

export const APPEAL_COLUMN_TITLE = 'Nhịp hấp dẫn';
/** Câu gọi đúng tên (R5) — hiện ở tooltip tiêu đề cột và dưới khối "Chốt dàn tập". */
export const APPEAL_PROMISE_NOTE = 'Nhịp hấp dẫn dự kiến: một sự việc có trích nguồn mà tập hứa với người xem. Đây là nhãn biên tập dự kiến, không phải bằng chứng tập đã hay.';
export const APPEAL_CHANGE_NOTE = 'Đổi nhãn nhịp là đổi lời hứa với người xem: dàn tập đã chốt sẽ phải "Chốt lại".';
/** m2 — tooltip ô "Đã xem — không có nhịp": tập CHỈ thành "chưa chỉ ra nhịp" khi mọi ý đã có trích; ý chữ sửa tay (chưa có trích) thì tập vẫn "chưa kiểm" (BE xét NO_BASIS trước HAS_STALE). */
export const APPEAL_REVIEWED_NOTE = 'Đánh dấu đã xem và tập này không có nhịp. Tập chỉ chuyển từ "chưa kiểm" sang "chưa chỉ ra nhịp" khi mọi ý của tập đã có trích nguồn; còn ý chữ sửa tay (chưa có trích) thì tập vẫn "chưa kiểm".';
/** m3 — dòng báo dưới ý đang trỏ ghi chú động cơ không còn trong tổng quan mà lần Lưu này sẽ bỏ liên kết đó. */
export const APPEAL_NOTE_LINK_DROP = 'Ghi chú động cơ cũ của ý này không còn trong tổng quan — liên kết ghi chú cũ sẽ bị bỏ khi lưu.';
export const APPEAL_NO_CLAIMS_HINT = 'Tập chưa có ý kèm trích nguồn nên chưa gắn được nhịp — nhờ AI viết lại tập này trước.';

/** 4 mã lý do CHƯA KIỂM của BE (`AppealReason`) ⇒ một câu ngắn. Mã lạ / vắng ⇒ '' (ô chỉ hiện "chưa kiểm"). */
const REASON_SHORT: Record<string, string> = {
  NO_CLAIMS: 'tập chia trước khi có ý kèm trích',
  NO_BASIS: 'chưa khai nhịp (chia trước đợt này hoặc đã đổi ranh giới)',
  HAS_STALE: 'có ý chữ đã sửa tay / đổi ranh giới, chưa có trích',
  ALL_LABELED_UNVERIFIED: 'ý có nhãn chưa xác minh được trích',
};
export const appealReasonShort = (code: string | null | undefined): string => (code ? REASON_SHORT[code] ?? '' : '');

const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);
const PART_LABEL: Record<string, string> = { synopsis: 'Tóm tắt', hook: 'Móc cuối tập', suspense: 'Còn treo' };

// ─── ghi chú động cơ (tổng quan) ─────────────────────────────────────────────────────────────────────────────

export interface MotiveNoteOption { id: string; group: MotiveGroup; groupLabel: string; text: string; label: string }

export const motiveNoteOptions = (motives: OverviewMotives | null | undefined): MotiveNoteOption[] =>
  MOTIVE_GROUPS.flatMap((g) => (motives?.[g.group] ?? []).map((n): MotiveNoteOption => ({
    id: n.id, group: g.group, groupLabel: g.label, text: n.text, label: `${g.label}: ${clip(String(n.text ?? ''), 60)}`,
  })));

// ─── ô của cột "Nhịp hấp dẫn" ────────────────────────────────────────────────────────────────────────────────

export type AppealCellKind = 'none' | 'present' | 'empty' | 'unknown';
export interface AppealChip {
  claimId: string; part: 'synopsis' | 'hook'; kind: EpisodeAppealKind; label: string; color: string;
  /** Chữ ý, cắt gọn để vừa ô. */
  text: string;
  /** Mỗi phần tử một dòng của tooltip. */
  tooltip: string[];
}
export interface AppealCell { kind: AppealCellKind; chips: AppealChip[]; text?: string }

/** Ý mang nhãn hợp lệ: chỉ synopsis/hook, `kind` đúng một trong 3 loại (dữ liệu hỏng không thành thẻ). */
const labeledClaims = (e: Pick<OutlineEpisode, 'claims'>): Array<EpisodeClaim & { part: 'synopsis' | 'hook'; appeal: { kind: EpisodeAppealKind; noteId?: string } }> =>
  (e.claims ?? []).filter((c): c is EpisodeClaim & { part: 'synopsis' | 'hook'; appeal: { kind: EpisodeAppealKind; noteId?: string } } =>
    (c.part === 'synopsis' || c.part === 'hook') && isAppealKind(c.appeal?.kind));

const chipTooltip = (ep: number, c: EpisodeClaim & { appeal: { kind: EpisodeAppealKind; noteId?: string } }, appeal: EpisodeAppealView, motives: OverviewMotives | null | undefined): string[] => {
  const lines = [`${APPEAL_KIND_LABEL[c.appeal.kind]} — nhịp hấp dẫn dự kiến (${APPEAL_KIND_HINT[c.appeal.kind]})`, `Ý thuộc: ${PART_LABEL[c.part] ?? c.part}`];
  if (c.status === 'stale') lines.push('Chữ ý đã sửa tay — chưa có trích nguồn.');
  else if (c.status === 'unverified') lines.push('Ý chưa xác minh được trích nguồn.');
  if (c.origin === 'ADDED') lines.push('Ý thêm có chủ đích (không có trong nguồn).');
  else if (c.anchor?.quote) lines.push(`Trích nguồn: “${clip(c.anchor.quote, 240)}”`);
  const noteId = c.appeal.noteId;
  if (noteId) {
    const note = motiveNoteOptions(motives).find((n) => n.id === noteId);
    // Sha tổng quan lệch (hoặc ghi chú không còn) ⇒ KHÔNG dám nói chữ hiện tại của ghi chú: nó có thể không còn là ghi chú lúc nhịp được khai.
    const old = (appeal.staleNoteLinks ?? []).includes(ep) || !note;
    // m4: không in id máy (sm3…) — chỉ nhãn nhóm + chữ ghi chú khi dám nói; còn lại câu chung.
    lines.push(old ? 'Thực hiện một ghi chú động cơ (theo tổng quan cũ)' : `Thực hiện (${note!.groupLabel}): ${note!.text}`);
  }
  return lines;
};

/**
 * Ô của một tập. `appeal` vắng (BE cũ) hoặc không có khoá tập ⇒ `none` (hiện "—", không giả vờ "chưa kiểm").
 * PRESENT ⇒ thẻ cho từng ý có nhãn · EMPTY ⇒ "chưa chỉ ra nhịp" · UNKNOWN ⇒ "chưa kiểm" + lý do ngắn (+ các nhãn hiện có, nếu có).
 */
export const appealCellOf = (e: OutlineEpisode, appeal: EpisodeAppealView | null | undefined, motives: OverviewMotives | null | undefined): AppealCell => {
  const state = appeal?.states?.[String(e.ep)];
  if (!appeal || !state) return { kind: 'none', chips: [] };
  const chips = labeledClaims(e).map((c): AppealChip => ({
    claimId: c.id, part: c.part, kind: c.appeal.kind, label: APPEAL_KIND_LABEL[c.appeal.kind], color: APPEAL_KIND_COLOR[c.appeal.kind],
    text: clip(String(c.text ?? ''), 80), tooltip: chipTooltip(e.ep, c, appeal, motives),
  }));
  if (state === 'PRESENT') return { kind: 'present', chips };
  if (state === 'EMPTY') return { kind: 'empty', chips: [], text: 'chưa chỉ ra nhịp' };
  const why = appealReasonShort(appeal.reasons?.[String(e.ep)]);
  return { kind: 'unknown', chips, text: why ? `chưa kiểm · ${why}` : 'chưa kiểm' };
};

/** Một dòng chữ thuần của ô (màn hẹp / tooltip). Rỗng ⇒ không có gì để nói. */
const cellLine = (cell: AppealCell): string => {
  if (cell.kind === 'none') return '';
  if (cell.kind === 'present') return `Nhịp hấp dẫn dự kiến: ${cell.chips.map((c) => `${c.label} — ${c.text}`).join('; ')}`;
  return `Nhịp hấp dẫn: ${cell.text ?? ''}`;
};

/** Màn hẹp (không đủ chỗ cho cột Móc và cột Nhịp): gộp cả hai vào MỘT tooltip của ô Tóm tắt. Không có gì ⇒ undefined. */
export const appealNarrowTitle = (e: Pick<OutlineEpisode, 'hook'>, cell: AppealCell): string | undefined => {
  const lines = [e.hook ? `Móc cuối tập: ${e.hook}` : '', cellLine(cell)].filter(Boolean);
  return lines.length ? lines.join('\n') : undefined;
};

// ─── thẻ "Chốt dàn tập" ──────────────────────────────────────────────────────────────────────────────────────

/** "X/Y tập đã chỉ ra nhịp · Z tập chưa kiểm" (Y = tổng ba nhóm). Không có tập ⇒ ''. */
export const appealCoverageLine = (c: { present: number; empty: number; unknown: number }): string => {
  const total = c.present + c.empty + c.unknown;
  return total ? `${c.present}/${total} tập đã chỉ ra nhịp · ${c.unknown} tập chưa kiểm` : '';
};

/**
 * Một đoạn cực đại các tập liền chưa chỉ ra nhịp. 🔴 Độ dài chỉ là SỐ QUAN SÁT (chưa đo ngưỡng — C34): mọi đoạn cùng một kiểu dòng, không mức độ, không tô đỏ.
 */
export const appealRunLine = (r: AppealRun): string =>
  r.length > 1 ? `Tập ${r.from}–${r.to} (${r.length} tập liền) chưa chỉ ra nhịp` : `Tập ${r.from} chưa chỉ ra nhịp`;

export interface AppealRunLine { key: string; text: string; from: number; to: number; length: number }
export interface AppealPlanBlock {
  coverageLine: string;
  runLines: AppealRunLine[];
  note: string;
  /** Có đoạn nào ⇒ hiện nút "Hỏi trợ lý". */
  canAsk: boolean;
  askText: string;
}

/** Khối trong thẻ Chốt dàn tập. `appeal` vắng (BE cũ) hoặc 0 tập ⇒ null (không vẽ gì). Không bao giờ chặn nút Chốt. */
export const appealPlanBlock = (appeal: EpisodeAppealView | null | undefined): AppealPlanBlock | null => {
  if (!appeal) return null;
  const coverageLine = appealCoverageLine(appeal.coverage ?? { present: 0, empty: 0, unknown: 0 });
  if (!coverageLine) return null;
  const runs = appeal.runs ?? [];
  return {
    coverageLine,
    runLines: runs.map((r) => ({ key: `${r.from}-${r.to}`, text: appealRunLine(r), from: r.from, to: r.to, length: r.length })),
    note: APPEAL_PROMISE_NOTE, canAsk: runs.length > 0, askText: appealAssistantQuestion(runs),
  };
};

const MAX_RUNS_IN_QUESTION = 8;

/** Câu điền sẵn vào khung Trợ lý ④ (KHÔNG tự gửi): gợi ý gộp / dời điểm cắt — hai lệnh ④ có sẵn — và dặn không đảo thứ tự. Không đoạn nào ⇒ ''. */
export const appealAssistantQuestion = (runs: ReadonlyArray<AppealRun>): string => {
  if (!runs.length) return '';
  const shown = runs.slice(0, MAX_RUNS_IN_QUESTION).map((r) => (r.length > 1 ? `tập ${r.from}–${r.to} (${r.length} tập liền)` : `tập ${r.from}`)).join(', ');
  const more = runs.length > MAX_RUNS_IN_QUESTION ? `, …và ${runs.length - MAX_RUNS_IN_QUESTION} đoạn nữa` : '';
  return `Dàn tập có các đoạn chưa chỉ ra nhịp hấp dẫn nào: ${shown}${more}. `
    + 'Xem giúp: có nên gộp các tập kề nhau hoặc dời điểm cắt giữa hai tập (chỉ ở phần chưa viết) để nhịp hấp dẫn của tập sau tới sớm hơn không, '
    + 'hoặc nên viết lại tập để chỉ ra nhịp đã có trong nguồn? Giữ nguyên thứ tự sự việc, không đảo thứ tự, không bịa tình tiết ngoài đoạn nguồn.';
};

// ─── form "Sửa" tập ──────────────────────────────────────────────────────────────────────────────────────────

export type AppealFormKind = '' | EpisodeAppealKind;
export interface AppealFormRow { claimId: string; part: 'synopsis' | 'hook'; partLabel: string; text: string; kind: AppealFormKind; noteId: string }
export interface AppealDraftEntry { kind: AppealFormKind; noteId: string }
export type AppealDraft = Record<string, AppealDraftEntry>;
export type TextFields = { synopsis: string; hook: string; suspense: string };

/** Mỗi ý synopsis/hook CÓ TRÍCH NGUỒN một dòng (ý ADDED — do chuyển thể thêm — không gắn nhãn được, BE từ chối). Tập không có ý kèm trích ⇒ []. */
export const appealFormRows = (e: Pick<OutlineEpisode, 'claims'>): AppealFormRow[] =>
  (e.claims ?? []).filter((c) => (c.part === 'synopsis' || c.part === 'hook') && c.origin !== 'ADDED').map((c): AppealFormRow => ({
    claimId: c.id, part: c.part as 'synopsis' | 'hook', partLabel: PART_LABEL[c.part], text: String(c.text ?? ''),
    kind: isAppealKind(c.appeal?.kind) ? c.appeal!.kind : '', noteId: isAppealKind(c.appeal?.kind) ? c.appeal?.noteId ?? '' : '',
  }));

/** Lý do không gắn được nhịp (null = gắn được). BE từ chối tập không có `claims` bằng INVALID_OP. */
export const appealFormBlocker = (e: Pick<OutlineEpisode, 'claims'>): string | null => (e.claims?.length ? null : APPEAL_NO_CLAIMS_HINT);

export const initialAppealDraft = (rows: ReadonlyArray<AppealFormRow>): AppealDraft =>
  Object.fromEntries(rows.map((r) => [r.claimId, { kind: r.kind, noteId: r.noteId }]));

type TextPart = 'synopsis' | 'hook' | 'suspense';
/** Phần chữ người đã sửa (so với chữ đang lưu, bỏ khoảng trắng đầu/cuối — BE cũng `trim`). */
export const textDirtyParts = (e: Pick<OutlineEpisode, 'synopsis' | 'hook' | 'suspense'>, v: TextFields): Set<TextPart> =>
  new Set((['synopsis', 'hook', 'suspense'] as const).filter((p) => String(v[p] ?? '').trim() !== String(e[p] ?? '').trim()));

/**
 * Sửa chữ một phần ⇒ BE gộp MỌI ý của phần đó thành MỘT ý chưa có trích (`reconcileEpisodeClaims`) ⇒ nhãn của phần mất, dù chỉ sửa một dấu.
 * Mỗi phần (tóm tắt / móc) đang có nhãn mà chữ đổi ⇒ một cảnh báo. "Đang có nhãn" tính theo giá trị người đang CHỌN trong form (`draft`, nếu có) chứ không chỉ nhãn đã lưu —
 * nhãn vừa chọn trên phần đã sửa chữ cũng không tới được server (`buildEpisodeFormOps` bỏ) nên phải báo. Suspense không mang nhãn.
 */
export const lostLabelWarnings = (e: Pick<OutlineEpisode, 'claims' | 'synopsis' | 'hook' | 'suspense'>, v: TextFields, draft?: AppealDraft): string[] => {
  const dirty = textDirtyParts(e, v);
  const withLabel = new Set(appealFormRows(e).filter((r) => (draft?.[r.claimId]?.kind ?? r.kind) !== '').map((r) => r.part));
  return (['synopsis', 'hook'] as const).filter((p) => dirty.has(p) && withLabel.has(p))
    .map((p) => `Sửa chữ phần "${PART_LABEL[p]}": nhãn nhịp của ý trong phần này sẽ mất (ý thành chưa có trích) — cần khai lại sau khi AI viết lại tập.`);
};

export interface FormOpsInput {
  episode: OutlineEpisode;
  text: TextFields;
  draft: AppealDraft;
  /** id ghi chú động cơ có trong tổng quan HIỆN TẠI — BE từ chối noteId ngoài tập này (INVALID_OP). */
  knownNoteIds: ReadonlySet<string>;
  /** Người xác nhận "đã xem — tập này không có nhịp" (chỉ có hiệu lực khi tập chưa có khoá "đã khai"). */
  markReviewed?: boolean;
}

/**
 * Lệnh PATCH ④ của form "Sửa" tập (Q8-4). THỨ TỰ BẮT BUỘC: mọi `setEpisodeAppeal` TRƯỚC `editEpisodeText` — id ý theo bản đang xem, còn sửa chữ làm BE đánh lại id.
 *  - `setEpisodeAppeal` CHỈ cho ý ĐỔI (kind/noteId khác lúc mở form) — BE trả NOOP cho ý không đổi. Phần đã sửa chữ thì bỏ (nhãn mất ngay sau đó).
 *  - `noteId` không còn trong tổng quan ⇒ bỏ khỏi lệnh (gửi lại là INVALID_OP).
 *  - `editEpisodeText` gửi khi có chữ đổi, HOẶC khi không có lệnh nhãn nào (giữ nguyên hành vi cũ: bấm Lưu không đổi vẫn xoá cờ "cần chấm lại"). Lưu chỉ-nhãn thì KHÔNG đụng cờ đó.
 *  - Tập chưa khoá + `markReviewed` + không lệnh nhãn nào ⇒ một lệnh `kind:''` (BE đặt khoá: "đã khai, không nhịp").
 *  - Tập không có `claims` ⇒ không bao giờ có lệnh nhãn (BE từ chối).
 */
export const buildEpisodeFormOps = (p: FormOpsInput): EpisodeSplitOp[] => {
  const e = p.episode;
  const rows = appealFormRows(e);
  const dirty = textDirtyParts(e, p.text);
  const ops: EpisodeSplitOp[] = [];
  for (const r of rows) {
    if (dirty.has(r.part)) continue;
    const d = p.draft[r.claimId];
    if (!d || (d.kind !== '' && !isAppealKind(d.kind))) continue;
    const noteId = d.kind === '' ? '' : d.noteId ?? '';
    if (d.kind === r.kind && noteId === r.noteId) continue;
    ops.push({ op: 'setEpisodeAppeal', ep: e.ep, claimId: r.claimId, kind: d.kind, ...(d.kind !== '' && noteId && p.knownNoteIds.has(noteId) ? { noteId } : {}) });
  }
  // "Đã xem — không có nhịp": lệnh kind:'' chỉ cần MỘT ý synopsis/hook bất kỳ (kể cả ADDED — BE cho kind:'' qua) để đặt khoá.
  const anchorClaim = (e.claims ?? []).find((c) => c.part === 'synopsis' || c.part === 'hook');
  if (!ops.length && p.markReviewed && anchorClaim && !e.appealBasis) ops.push({ op: 'setEpisodeAppeal', ep: e.ep, claimId: anchorClaim.id, kind: '' });
  if (dirty.size > 0 || !ops.length) ops.push({ op: 'editEpisodeText', ep: e.ep, synopsis: p.text.synopsis, hook: p.text.hook, suspense: p.text.suspense });
  return ops;
};

/**
 * m3 — ý (claimId) mà lần Lưu này sẽ BỎ liên kết ghi chú cũ: ý đang trỏ `noteId` không còn trong tổng quan, người đổi loại nhưng giữ nhãn (op không mang `noteId` chết).
 * Gỡ hẳn nhãn (`kind:''`) thì liên kết đi theo nhãn — không cần báo riêng. Người không đụng ý đó ⇒ không op ⇒ liên kết giữ nguyên ⇒ không báo.
 */
export const droppedNoteLinkClaimIds = (episode: OutlineEpisode, ops: ReadonlyArray<EpisodeSplitOp>, knownNoteIds: ReadonlySet<string>): Set<string> => {
  const out = new Set<string>();
  const orig = new Map(appealFormRows(episode).map((r) => [r.claimId, r.noteId]));
  for (const o of ops) {
    if (o.op !== 'setEpisodeAppeal' || o.kind === '' || o.noteId) continue;
    const was = orig.get(o.claimId);
    if (was && !knownNoteIds.has(was)) out.add(o.claimId);
  }
  return out;
};

/** Có lệnh đổi nhãn ⇒ dàn tập đã chốt sẽ phải "Chốt lại" (hiện câu báo trước khi Lưu). */
export const willChangeAppeal = (ops: ReadonlyArray<EpisodeSplitOp>): boolean => ops.some((o) => o.op === 'setEpisodeAppeal');
